import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { setWording, subscribeWords, wordsVersion, type Wording } from '../lib/i18n/catalog'
import { api, type PrefetchFile } from '../lib/api'
import { slipsKey, type SlipsByService } from '../lib/slips'
import { holdFiles } from '../lib/screenWorker'
import type { ScreenAppAnswer } from '../lib/types'
import { answerOrder, drawnKey, reaskDelay } from './playlist'
import type { LayoutDocument, LayoutKind } from './types'

/**
 * The layout a screen should be showing.
 *
 * The server always answers with a document — the customer's if they have published
 * one, the built-in design otherwise — so a screen never has to decide what to do
 * with "no layout". That decision is made once, on the server, where it can be
 * explained.
 *
 * The version is re-checked on a timer. A hall board is a browser nobody can reach:
 * it hangs on a wall, it has no keyboard, and the person who has just redesigned it
 * is standing in the administration screen in another building. Polling every half
 * minute is how their change arrives without anybody fetching a ladder.
 *
 * The poll stays the fallback. The socket carries only a data-free "ask again" after a
 * campaign or playlist change, coalesced per branch on the server, so it never loads the queue's path.
 */
const RECHECK_MS = 30_000

/** A nudged branch's boards ask again within this, at random, so they do not all ask at once. */
export const NUDGE_JITTER_MS = 3_000

/** One reload per burst of nudges: the first waits a random part of the jitter, later ones join it. */
export function nudgeGate(random: () => number = Math.random) {
  let timer: ReturnType<typeof setTimeout> | undefined
  return {
    nudge(reload: () => void) {
      if (timer !== undefined) return
      timer = setTimeout(() => {
        timer = undefined
        reload()
      }, Math.floor(random() * NUDGE_JITTER_MS))
    },
    cancel() {
      clearTimeout(timer)
      timer = undefined
    },
  }
}

/**
 * `screen` is the address of an unattended machine, and changes which endpoint is
 * asked. A kiosk in a lobby and a board on a wall have no account, so the
 * signed-in layout route answers them with a 401 — and a screen with no document
 * renders nothing at all, which is what both were doing in production: a blank
 * page where the hall should have been.
 */
export function useLayout(
  branchId: string | undefined,
  kind: LayoutKind,
  screen?: { branchCode: string; unitCode: string },
  nudge?: number,
) {
  const [document, setDocument] = useState<LayoutDocument | null>(null)
  // The current effect's load, for a nudge from the live channel.
  const reload = useRef<() => void>(() => {})
  const gate = useRef(nudgeGate())
  const [version, setVersion] = useState(0)
  // The organisation's words arrive with the design; a change re-renders the screen.
  useSyncExternalStore(subscribeWords, wordsVersion, wordsVersion)
  // Which design that is: '' for the built-in one. Reported back, with the version,
  // so the editor can say which machines have drawn a publish.
  const [layoutId, setLayoutId] = useState('')
  // What this machine's paper looks like, for a kiosk. Kept beside the screen's
  // own design because it arrives in the same answer: a kiosk needs it before the
  // first customer touches anything, and a second request is a second thing that
  // can be missing at the moment paper is due.
  const [ticket, setTicket] = useState<LayoutDocument | null>(null)
  // The services that print something other than the machine's own slip. Kept
  // beside ticket, and replaced by the same fingerprint check, so a poll that
  // changed nothing does not hand the kiosk new objects for designs it already has.
  const [ticketByService, setTicketByService] = useState<SlipsByService>({})
  const ticketKey = useRef('')
  // What is drawn (playlist.ts drawnKey), the timer that asks again when a
  // playlist's time changes, and which change that timer last asked about.
  const shown = useRef('')
  const reask = useRef<number | undefined>(undefined)
  const askedFor = useRef<string | undefined>(undefined)
  // Whether an addressed kiosk's branch has bookings still to arrive today — the
  // "I have a booking" button. Asked with the design, so it is at most half a
  // minute behind the last arrival or import.
  const [bookings, setBookings] = useState(false)
  // The screen app placed on this machine, when one is. Replaced only when the
  // version or the placement's values changed: a new object every half minute
  // would restart the app in the middle of a customer's visit, for nothing.
  const [app, setApp] = useState<ScreenAppAnswer | null>(null)
  const appKey = useRef('')
  // The last answer, drawn again when the server says it is unchanged.
  const held = useRef<LayoutAnswer | null>(null)
  const branchCode = screen?.branchCode
  const unitCode = screen?.unitCode

  useEffect(() => {
    // An addressed screen needs no branch id: its address is its identity, and
    // waiting for the id would delay the first paint by a round trip.
    if (!branchId && !(branchCode && unitCode)) return
    let live = true
    // A screen that changes branch or address draws its first answer, whatever
    // the last one was.
    shown.current = ''
    askedFor.current = undefined
    held.current = null
    const order = answerOrder()

    const load = async () => {
      const mine = order.ask()
      try {
        const fresh: LayoutAnswer | undefined =
          branchCode && unitCode
            ? await api.screenLayout(branchCode, unitCode, held.current?.etag)
            : await api.branchLayout(branchId!, kind)
        // A slow answer overtaken by a newer one is dropped, so it cannot put back
        // a list the board has already moved past.
        if (!live || !order.take(mine)) return
        const answer = fresh ?? held.current
        if (!answer) return
        held.current = answer
        if (branchCode && unitCode) setWording((answer as { wording?: Wording }).wording)

        // A missing `prefetch` is the server's fault path, not an empty list: the worker keeps what it holds.
        const prefetch = (answer as { prefetch?: PrefetchFile[] }).prefetch
        if (branchCode && unitCode && prefetch) {
          holdFiles(`${branchCode}/${unitCode}`, prefetch, api.screenAssetURL(branchCode, unitCode))
        }
        // Replaced only when what is drawn changed: the version, or the items a
        // playlist is playing. A board repainting itself every thirty seconds
        // would restart the highlight on a called number and flicker a logo that
        // has not moved; a board comparing only the version would never move to
        // the lunchtime list.
        //
        // The design's identity is part of it. Since a branch chooses its design
        // from a library (0062), a machine can move from one design at version 2
        // to another at version 2, and comparing the version alone would leave it
        // drawing the old one until somebody published the new one again.
        const drawn = shownKey(answer.layoutId, answer.version, answer.document)
        if (drawn !== shown.current) {
          shown.current = drawn
          setDocument(answer.document)
          setVersion(answer.version)
          setLayoutId(answer.layoutId ?? '')
        }

        // Ask again the moment the list changes, rather than up to half a minute
        // after it. A board that cannot reach the server then keeps what it has,
        // and the poll below tries again. Every answer replaces the timer, so there
        // is only ever one, and it is for the change the latest answer names.
        window.clearTimeout(reask.current)
        reask.current = undefined
        const changesAt = answer.changesAt
        const delay = reaskDelay(changesAt, Date.now(), Math.random() * 2000, askedFor.current)
        if (delay !== null) {
          reask.current = window.setTimeout(() => {
            askedFor.current = changesAt
            void load()
          }, delay)
        }
        // What this machine's paper looks like, and which services print
        // something else. An addressed kiosk has it under `ticket`; reception
        // asks the signed-in route for kind 'ticket' and has it at the top.
        // Every other signed-in screen is told nothing about paper.
        //
        // Replaced only when a version changed. Setting new objects on every poll
        // re-ran the kiosk's picture fetch twice a minute, for designs that had
        // not changed since the morning.
        const paper = answer as {
          ticket?: { document: LayoutDocument; version: number; layoutId?: string; byService?: SlipsByService }
          byService?: SlipsByService
        }
        const addressedPaper = branchCode && unitCode ? paper.ticket : undefined
        // Only an addressed machine runs an app. The signed-in routes never send
        // one, and an operator previewing the kiosk sees the design.
        const nextApp = (branchCode && unitCode ? (answer as { app?: ScreenAppAnswer }).app : undefined) ?? null
        const nextAppKey = screenAppKey(nextApp)
        if (nextAppKey !== appKey.current) {
          appKey.current = nextAppKey
          setApp(nextApp)
        }
        setBookings(Boolean(branchCode && unitCode) && (answer as { bookings?: boolean }).bookings === true)
        const nextTicket = addressedPaper?.document ?? null
        const nextByService =
          (addressedPaper ? addressedPaper.byService : kind === 'ticket' ? paper.byService : undefined) ?? {}
        // Keyed by which slip as well as its version, for the same reason as
        // the screen's own design above.
        const key = `${nextTicket ? `${addressedPaper?.layoutId ?? ''}:${addressedPaper?.version ?? 0}` : -1}|${
          kind === 'ticket' ? (answer.layoutId ?? '') : ''
        }|${slipsKey(
          kind === 'ticket' ? answer.version : 0,
          nextByService,
        )}`
        if (key !== ticketKey.current) {
          ticketKey.current = key
          setTicket(nextTicket)
          setTicketByService(nextByService)
        }
      } catch {
        // A screen that cannot reach the server keeps showing what it has. The
        // connection badge already says the link is down; blanking the board as
        // well would be two failures for one fault.
      }
    }

    void load()
    reload.current = () => void load()
    const timer = window.setInterval(load, RECHECK_MS)
    return () => {
      live = false
      reload.current = () => {}
      window.clearInterval(timer)
      window.clearTimeout(reask.current)
      reask.current = undefined
    }
  }, [branchId, kind, branchCode, unitCode])

  useEffect(() => {
    if (nudge) gate.current.nudge(() => reload.current())
  }, [nudge])
  useEffect(() => {
    const g = gate.current
    return () => g.cancel()
  }, [])

  return { document, version, layoutId, ticket, ticketByService, bookings, app }
}

type LayoutAnswer =
  | NonNullable<Awaited<ReturnType<typeof api.screenLayout>>>
  | (Awaited<ReturnType<typeof api.branchLayout>> & { etag?: string })

/**
 * What decides whether a screen redraws its design: which design, its version, and
 * what its playlists are playing (playlist.ts drawnKey). The built-in design has
 * no identifier and is version 0.
 */
export function shownKey(layoutId: string | undefined, version: number, doc: LayoutDocument): string {
  return `${layoutId ?? ''}|${drawnKey(version, doc)}`
}

/**
 * What decides whether a running app is restarted: which version, and the values
 * it was placed with. An app reads its attributes once, from context(), so a
 * changed value reaches a running app only by starting it again.
 */
export function screenAppKey(app: ScreenAppAnswer | null | undefined): string {
  if (!app) return ''
  return `${app.versionId}|${app.url}|${JSON.stringify(app.attributes ?? {})}`
}
